import {
  useState, MouseEvent, useEffect, useLayoutEffect, useRef,
} from 'react';
import { IconLoader } from '@tabler/icons-react';

import { MapType } from '../../types/enums';
import {
  FrontendGeoJsonFeature, HouseholdGroup, MapHeadlines as Headlines, MapClient,
} from '../../types/global';
import PostStreetviewWards from '../../services/adapters/streetviewWards';
import PostStreetviewLocalAuthorities from '../../services/adapters/streetviewLocalAuthorities';
import useFilterStore from './filterStore';
import PostStreetviewHouseholds from '../../services/adapters/streetviewHouseholds';
import useDebounce from '../../hooks/useDebounce';
import GetLatestSnapshot from '../../services/adapters/snapshotResponse';

import BaseMap from '../map';
import Tooltip from '../toolTip';
import { TakeActionMenu } from '../../features/streetview';
import MapToggle from './mapToggle';

export default function StreetviewMap({ mapConfig }: Readonly<{ mapConfig: MapClient }>) {
  const [mapType, setMapType] = useState<MapType>(mapConfig.mapType ?? MapType.WardMap);
  const [snapshot, setSnapshot] = useState<string>('');
  const [areaData, setAreaData] = useState<FrontendGeoJsonFeature[]>([]);
  const [householdGroups, setHouseholdGroups] = useState<HouseholdGroup[]>([]);
  const [mapHeadlines, setMapHeadlines] = useState<Headlines[]>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const headlineMetricsRef = useRef<HTMLDivElement>(null);
  const [headlineMetricsBottom, setHeadlineMetricsBottom] = useState(0);

  useLayoutEffect(() => {
    const headline = headlineMetricsRef.current;
    if (!headline) return undefined;
    const measure = () => setHeadlineMetricsBottom(headline.offsetTop + headline.offsetHeight);
    const observer = new ResizeObserver(measure);
    observer.observe(headline);
    measure();
    return () => observer.disconnect();
  }, []);

  const currentFilters = useFilterStore((state) => state.selectedFilters);
  const timeDelay = 500;
  const debouncedFilters = useDebounce(currentFilters, timeDelay);

  useEffect(() => {
    const abortController = new AbortController();

    const getSnapshot = async () => {
      const snapshotAdapter = new GetLatestSnapshot();
      const response = await snapshotAdapter.adaptResponse();
      setSnapshot(response.formattedSnapshot);
    };

    const getMapData = async (type: MapType) => {
      setErrorMessage(null);
      setLoading(true);
      try {
        if (type === MapType.WardMap || type === MapType.LocalAuthorityMap) {
          const streetviewAreaAdapter = type === MapType.WardMap
            ? new PostStreetviewWards()
            : new PostStreetviewLocalAuthorities();
          const response = await streetviewAreaAdapter.adaptResponse(
            {
              data: debouncedFilters,
              signal: abortController.signal,
            },
          );

          if (!abortController.signal.aborted) {
            setAreaData(response.geoJsonFeatures);
            setMapHeadlines(response.mapHeadlines);
            setLoading(false);
          }
        }
        if (type === MapType.HouseholdMap) {
          const streetviewHouseholdAdapter = new PostStreetviewHouseholds();
          const response = await streetviewHouseholdAdapter.adaptResponse(
            {
              data: debouncedFilters,
              signal: abortController.signal,
            },
          );
          if (!abortController.signal.aborted) {
            setHouseholdGroups(response.householdGroups);
            setMapHeadlines(response.mapHeadlines);
            setLoading(false);
          }
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          setMapHeadlines([]);
          setLoading(false);
          setErrorMessage((error as Error).message || 'An error occurred while fetching data.');
        }
      }
    };

    void getMapData(mapType);
    void getSnapshot();

    return () => {
      abortController.abort();
    };
  }, [mapType, debouncedFilters]);

  const handleMapTypeChange = (e: MouseEvent<HTMLButtonElement>) => {
    const selectedMapType = e.currentTarget.value as MapType;
    if (selectedMapType === mapType) return;

    // Ward and local authority maps share the area layer, so drop the previous shapes rather than show them under the new label
    setAreaData([]);
    setMapType(selectedMapType);
  };

  const renderHeadlines = () => {
    if (!mapHeadlines) {
      return (
        <div className="flex justify-center items-center">
          <IconLoader className="animate-spin w-5 h-5 text-blue-500 m-2" />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <ul className="flex flex-row divide-x-2 divide-pipgrey-border">
          <li className="flex flex-col justify-center items-center text-center p-2 flex-1 text-piptext-error">
            {errorMessage}
          </li>
        </ul>
      );
    }

    if (loading) {
      return (
        <ul className="flex flex-row divide-x-2 divide-pipgrey-border">
          <li className="flex justify-center items-center p-2 flex-1">
            <IconLoader className="animate-spin w-10 h-10 text-primary-dark" />
          </li>
        </ul>
      );
    }

    return (
      <ul className="flex flex-row divide-x-2 divide-pipgrey-border items-center">
        {mapHeadlines.map((headline) => (
          <li key={headline.displayName} className="flex flex-col justify-center items-center text-center p-2 flex-1">
            <span className="font-thin text-xs">
              {headline.displayName}
            </span>
            <span className="font-semibold">
              {headline.value}
            </span>
            {headline.description.length > 1 && <Tooltip label={headline.displayName} text={headline.description} />}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div
      id="map-header-container"
      className="relative flex flex-col h-full w-full transition-all duration-500 ease-in-out order-first"
    >
      <header id="map-header" className="relative h-16 flex items-center justify-between pr-6 shadow">
        <MapToggle mapType={mapType} setMapType={handleMapTypeChange} />
        {snapshot ? (<span className="font-thin">{`${snapshot} data`}</span>) : ''}
        <TakeActionMenu filters={currentFilters} />
      </header>

      <div id="streetview-map-container" className="relative h-full min-h-[300px] flex flex-col items-center overflow-hidden">
        <BaseMap
          id="streetview"
          mapConfig={mapConfig}
          mapType={mapType}
          areaData={areaData}
          householdGroups={householdGroups}
          mapZoom={10}
          noCardZoneHeight={headlineMetricsBottom}
        />
        <div
          ref={headlineMetricsRef}
          id="headline-summary"
          className="absolute mt-4 h-fit w-fit opacity-90 bg-pipbg border border-pipgrey-border rounded-lg"
        >
          {renderHeadlines()}
        </div>
      </div>
    </div>
  );
}
